import { ApiClient, createTokenConfig } from 'api/ApiClient';
import {
    PAYMENT_METHODS_CREATE_SESSION_ERROR,
    PAYMENT_METHODS_CREATE_SESSION_START,
    PAYMENT_METHODS_CREATE_SESSION_SUCCESS,
    PAYMENT_METHODS_FETCH_ERROR,
    PAYMENT_METHODS_FETCH_START,
    PAYMENT_METHODS_FETCH_SUCCESS,
    PAYMENT_METHODS_RESET,
    PAYMENT_METHODS_SURCHARGE_QUOTE_CLEAR,
    PAYMENT_METHODS_SURCHARGE_QUOTE_ERROR,
    PAYMENT_METHODS_SURCHARGE_QUOTE_START,
    PAYMENT_METHODS_SURCHARGE_QUOTE_SUCCESS,
    PAYMENT_METHODS_TOKEN_PAYMENT_ERROR,
    PAYMENT_METHODS_TOKEN_PAYMENT_RESET,
    PAYMENT_METHODS_TOKEN_PAYMENT_START,
    PAYMENT_METHODS_TOKEN_PAYMENT_SUCCESS,
    PaymentMethodsAction
} from 'actions/paymentMethods/PaymentMethodsActionsTypes';
import {
    dtoToAdyenSession,
    dtoToPaymentMethod,
    dtoToSurchargeQuote
} from 'actions/paymentMethods/PaymentMethodsConverter';
import {
    AdyenSessionDto,
    PaymentMethodDto,
    SurchargeQuoteDto,
    TokenPaymentRequestDto
} from 'actions/paymentMethods/PaymentMethodsDtos';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';

export const resetPaymentMethods = (): PaymentMethodsAction => ({
    type: PAYMENT_METHODS_RESET
});

export const clearSurchargeQuote = (): PaymentMethodsAction => ({
    type: PAYMENT_METHODS_SURCHARGE_QUOTE_CLEAR
});

export const resetTokenPayment = (): PaymentMethodsAction => ({
    type: PAYMENT_METHODS_TOKEN_PAYMENT_RESET
});

export const fetchPaymentMethodsThunk =
    (customerId: number): ThunkAction<Promise<void>, RootState, null, RootAction> =>
    async (dispatch, getState) => {
        try {
            dispatch({ type: PAYMENT_METHODS_FETCH_START });

            const marketplaceId = getState().marketplace.data.id;

            const response = await ApiClient.get<Array<PaymentMethodDto>>(
                `/customers/${customerId}/payment_methods`,
                {
                    ...createTokenConfig(),
                    params: { marketplace_id: marketplaceId }
                }
            );

            const paymentMethods = (response.data ?? []).map(dtoToPaymentMethod);

            dispatch({
                type: PAYMENT_METHODS_FETCH_SUCCESS,
                payload: { paymentMethods }
            });
        } catch (error) {
            console.error(error);
            dispatch({ type: PAYMENT_METHODS_FETCH_ERROR });
        }
    };

export const createPaymentMethodSessionThunk =
    (customerId: number): ThunkAction<Promise<void>, RootState, null, RootAction> =>
    async (dispatch, getState) => {
        try {
            dispatch({ type: PAYMENT_METHODS_CREATE_SESSION_START });

            const marketplaceId = getState().marketplace.data.id;

            const response = await ApiClient.post<AdyenSessionDto>(
                `/customers/${customerId}/payment_methods`,
                {},
                {
                    ...createTokenConfig(),
                    params: { marketplace_id: marketplaceId }
                }
            );

            const session = dtoToAdyenSession(response.data);

            dispatch({
                type: PAYMENT_METHODS_CREATE_SESSION_SUCCESS,
                payload: { session }
            });
        } catch (error) {
            console.error(error);
            dispatch({ type: PAYMENT_METHODS_CREATE_SESSION_ERROR });
        }
    };

export const fetchSurchargeQuoteThunk =
    (
        customerId: number,
        paymentMethodId: number,
        amount: number
    ): ThunkAction<Promise<void>, RootState, null, RootAction> =>
    async (dispatch, getState) => {
        try {
            dispatch({
                type: PAYMENT_METHODS_SURCHARGE_QUOTE_START,
                payload: { paymentMethodId }
            });

            const marketplaceId = getState().marketplace.data.id;

            const response = await ApiClient.get<SurchargeQuoteDto>(
                `/customers/${customerId}/payment_methods/${paymentMethodId}/surcharge_quote`,
                {
                    ...createTokenConfig(),
                    params: {
                        amount,
                        marketplace_id: marketplaceId
                    }
                }
            );

            const quote = dtoToSurchargeQuote(response.data, paymentMethodId);

            dispatch({
                type: PAYMENT_METHODS_SURCHARGE_QUOTE_SUCCESS,
                payload: { quote }
            });
        } catch (error) {
            console.error(error);
            dispatch({ type: PAYMENT_METHODS_SURCHARGE_QUOTE_ERROR });
        }
    };

export const payWithTokenThunk =
    (
        invoiceUuid: string,
        pspToken: string,
        amount: number
    ): ThunkAction<Promise<void>, RootState, null, RootAction> =>
    async (dispatch, getState) => {
        try {
            dispatch({ type: PAYMENT_METHODS_TOKEN_PAYMENT_START });

            const marketplaceId = getState().marketplace.data.id;

            const body: TokenPaymentRequestDto = {
                uuid: invoiceUuid,
                psp_token: pspToken,
                amount
            };

            await ApiClient.post('/invoices/token_payment', body, {
                ...createTokenConfig(),
                params: { marketplace_id: marketplaceId }
            });

            dispatch({ type: PAYMENT_METHODS_TOKEN_PAYMENT_SUCCESS });
        } catch (error) {
            console.error(error);
            dispatch({ type: PAYMENT_METHODS_TOKEN_PAYMENT_ERROR });
        }
    };
